import React, { useEffect, useState } from "react";
import TableSkeleton from "../../../core/common/TableSkeleton";
import { useDispatch, useSelector } from "react-redux";
import { Table, DatePicker, Button, Tag } from "antd";
import { fetchCallRecords } from "../../../core/data/redux/slices/didlogicSlice";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;

const CallHistory = () => {
  const dispatch = useDispatch();
  const { callRecords, callPagination, callLoading } = useSelector((s) => s.didlogic);
  const [dateRange, setDateRange] = useState([null, null]);
  const [page, setPage] = useState(1);

  useEffect(() => {
    dispatch(fetchCallRecords({ page, perPage: 100 }));
  }, [dispatch, page]);

  const handleFilter = () => {
    const params = { page: 1, perPage: 100 };
    if (dateRange[0]) params.from = dateRange[0].format("YYYY-MM-DD");
    if (dateRange[1]) params.to = dateRange[1].format("YYYY-MM-DD");
    setPage(1);
    dispatch(fetchCallRecords(params));
  };

  const formatDuration = (secs) => {
    if (!secs) return <span className="text-muted">—</span>;
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}m ${s}s`;
  };

  const columns = [
    {
      title: "Date / Time",
      dataIndex: "timestamp",
      key: "timestamp",
      render: (v) => (v ? new Date(v).toLocaleString() : "—"),
      width: 170,
    },
    {
      title: "From",
      dataIndex: "from",
      key: "from",
      render: (v) => <span className="font-monospace">{v || "—"}</span>,
    },
    {
      title: "To",
      dataIndex: "to",
      key: "to",
      render: (v) => <span className="font-monospace">{v || "—"}</span>,
    },
    {
      title: "Destination",
      dataIndex: "destination_name",
      key: "destination_name",
      render: (v) => v || "—",
    },
    {
      title: "Duration",
      dataIndex: "duration",
      key: "duration",
      render: formatDuration,
      align: "center",
    },
    {
      title: "Status",
      key: "status",
      render: (_, r) => {
        const answered = r.duration > 0;
        return <Tag color={answered ? "green" : "red"}>{answered ? "Answered" : "Not Answered"}</Tag>;
      },
      align: "center",
    },
    {
      title: "Cost",
      key: "cost",
      render: (_, r) => (
        <div>
          <div className="fw-semibold">${Number(r.ourCost ?? r.amount ?? 0).toFixed(4)}</div>
          {r.ourCost !== r.amount && r.amount > 0 && (
            <small className="text-muted">Base: ${Number(r.amount || 0).toFixed(4)}</small>
          )}
        </div>
      ),
      align: "right",
    },
  ];

  const totalPages = callPagination?.total_pages || 1;
  const totalRecords = callPagination?.total_records || callRecords.length;

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="row align-items-center mb-4">
          <div className="col">
            <h4 className="fw-bold mb-0">Call History</h4>
            <p className="text-muted mb-0">
              All outbound call records
              {totalRecords > 0 && <span className="ms-2 badge bg-secondary">{totalRecords.toLocaleString()} total</span>}
            </p>
          </div>
        </div>

        <div className="card mb-3">
          <div className="card-body py-3">
            <div className="row g-3 align-items-end">
              <div className="col-md-5">
                <label className="form-label small mb-1">Date Range</label>
                <RangePicker
                  className="w-100"
                  onChange={(dates) => setDateRange(dates || [null, null])}
                  disabledDate={(d) => d && d > dayjs().endOf("day")}
                />
              </div>
              <div className="col-md-2">
                <Button type="primary" onClick={handleFilter} className="w-100">
                  Filter
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body p-0">
            {callLoading ? (
              <TableSkeleton
                rows={10}
                rowHeight={42}
                columns={[
                  { width: 150, type: "text" },
                  { width: 110, type: "text" },
                  { width: 110, type: "text" },
                  { width: 120, type: "text" },
                  { width: 70,  type: "text" },
                  { width: 90,  type: "badge" },
                  { width: 70,  type: "text" },
                ]}
              />
            ) : (
            <Table
              dataSource={callRecords}
              columns={columns}
              rowKey={(r, i) => `${r.timestamp}-${r.to}-${i}`}
              loading={false}
              pagination={{
                pageSize: 100,
                current: page,
                total: totalRecords,
                onChange: setPage,
                showTotal: (total) => `${total.toLocaleString()} calls`,
              }}
              scroll={{ x: 900 }}
              locale={{ emptyText: "No call records found." }}
            />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CallHistory;
